/*
 * Copyright (c) 2022 Omnicell. All rights reserved.
 *
 * This program contains proprietary, trade secret information of
 * Omnicell. and may not be copied, reproduced or reverse engineered
 * in any way without the prior written permission of Omnicell.
 */

import moment from 'moment';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { DATA } from '../../../constants/messageTypes';
import { DataContext } from '../../../contexts/DataContext';
import {
  disableAllCardActions,
  registerTimeslotGridUpdater,
  sendMessageToBot,
  unregisterTimeslotGridUpdater
} from '../../../utils/messaging-helpers';
import TimeSlot from './TimeSlot';

const TimeSlotGrid = ({ timeslots: initialTimeslots, flowIdentifier, language }) => {
  const [canRender, setCanRender] = useState(true);
  const [timeInfo, setTimeInfo] = useState(null);
  const [currentTimeslots, setCurrentTimeslots] = useState(initialTimeslots);
  const { currentFlowIdentifier } = useContext(DataContext);
  const gridRef = useRef(null);

  // Register updater so handleGrid can swap timeslots in place without re-rendering a new message
  useEffect(() => {
    registerTimeslotGridUpdater(newTimeslots => {
      setCurrentTimeslots(newTimeslots);
      setTimeInfo(null);
    });
    return () => unregisterTimeslotGridUpdater();
  }, []);

  useEffect(() => {
    setTimeInfo(null);
    if (flowIdentifier !== currentFlowIdentifier) {
      setCanRender(false);
      unregisterTimeslotGridUpdater();
    }
  }, [flowIdentifier, currentFlowIdentifier]);

  const slots = useMemo(() => {
    if (!currentTimeslots) return [];

    const slotsMap = new Map();
    const configMoment = moment({
      year: currentTimeslots[0].startTime.date.year,
      month: currentTimeslots[0].startTime.date.month - 1, // zero-based
      date: currentTimeslots[0].startTime.date.day
    });

    currentTimeslots.forEach(ts => {
      const isOpen = ts.status === 'open';
      const slotStartMoment = configMoment.clone().set({ ...ts.startTime.time });
      const startTime = slotStartMoment.format('HH:mm:ss');
      const displayTime = slotStartMoment.format('h:mm A');
      const slotInfo = `${startTime}`;
      const remainingSlots = ts.concurrentAppointments - ts.scheduledAppointments;

      if (!slotsMap.has(startTime)) {
        slotsMap.set(startTime, {
          slotStartMoment,
          startTime,
          slotInfo,
          displayTime,
          isOpen,
          remainingSlots
        });
      } else if (!slotsMap.get(startTime).isOpen && isOpen) {
        slotsMap.set(startTime, {
          slotStartMoment,
          startTime,
          slotInfo,
          displayTime,
          isOpen,
          remainingSlots
        });
      }
    });

    return [...slotsMap.values()]
      .sort((a, b) => a.slotStartMoment.valueOf() - b.slotStartMoment.valueOf())
      .map(({ slotInfo, startTime, displayTime, isOpen, remainingSlots }) => (
        <TimeSlot
          key={slotInfo}
          startTime={startTime}
          slotInfo={slotInfo}
          displayTime={displayTime}
          isOpen={isOpen}
          remainingSlots={remainingSlots}
          isSelected={timeInfo === slotInfo}
          isDisabled={timeInfo !== null}
          setTimeInfo={setTimeInfo}
          language={language}
        />
      ));
  }, [currentTimeslots, timeInfo]);

  const availableTimes = useMemo(
    () => slots.filter(slot => slot.props.isOpen).map(slot => slot.props.slotInfo),
    [slots]
  );

  useEffect(() => {
    if (canRender && availableTimes.includes(timeInfo)) {
      disableAllCardActions();
      // Unregister so that pressing Back will render a new TimeSlotGrid
      // at the correct position rather than updating this (now stale) one
      unregisterTimeslotGridUpdater();
      sendMessageToBot(DATA, {
        // TODO: send the action to get ID dynamically
        data: { time: timeInfo },
        id: 'time_select'
      });
    }
  }, [timeInfo]);

  // Auto-focus first available timeslot when grid renders
  useEffect(() => {
    if (slots.length > 0 && gridRef.current) {
      setTimeout(() => {
        const firstTimeSlot = gridRef.current.querySelector('.time-slot:not(.disabled)');
        if (firstTimeSlot) {
          firstTimeSlot.focus();
        }
      }, 100);
    }
  }, [slots.length]);

  return canRender ? (
    <div className="time-slot-grid" ref={gridRef}>
      <div className="time-slot-list-wrapper">{slots}</div>
    </div>
  ) : null;
};

export default React.memo(TimeSlotGrid);
